import { T, Var } from 'gt-react';
import type { ReactNode } from 'react';
import type { TaskMetricAgingBucket } from '@erp/shared-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { TaskAgingCounts } from './taskMetricCardViews';

/** One age bucket on an aging card: its key (a day range) and colours. */
export interface AgingBucketView<K extends string> {
  key: K;
  barClass: string;
  textClass: string;
}

const TASK_AGING_BUCKETS: ReadonlyArray<
  AgingBucketView<TaskMetricAgingBucket>
> = [
  { key: '0-2', barClass: 'bg-emerald-500', textClass: 'text-emerald-700' },
  { key: '3-7', barClass: 'bg-amber-400', textClass: 'text-amber-700' },
  { key: '8-14', barClass: 'bg-orange-500', textClass: 'text-orange-700' },
  { key: '15+', barClass: 'bg-red-600', textClass: 'text-red-700' },
];

interface AgingPickProps<K extends string> {
  aging: Record<K, number>;
  buckets: ReadonlyArray<AgingBucketView<K>>;
  active: K | undefined;
  /** Absent: the card is read-only (no filtering). */
  onPick: ((bucket: K) => void) | undefined;
}

function AgingBar<K extends string>({
  aging,
  buckets,
  active,
  onPick,
}: AgingPickProps<K>) {
  return (
    <div className="flex h-2 gap-0.5 overflow-hidden rounded bg-muted">
      {buckets.flatMap((bucket) => {
        // Empty buckets get no segment.
        if (aging[bucket.key] <= 0) return [];
        const style = { flexGrow: aging[bucket.key] };
        return onPick ? (
          <button
            key={bucket.key}
            type="button"
            aria-label={bucket.key}
            aria-pressed={active === bucket.key}
            onClick={() => onPick(bucket.key)}
            className={`h-full cursor-pointer ${bucket.barClass} ${
              active && active !== bucket.key ? 'opacity-30' : ''
            }`}
            style={style}
          />
        ) : (
          <span
            key={bucket.key}
            className={`h-full ${bucket.barClass}`}
            style={style}
          />
        );
      })}
    </div>
  );
}

function LegendEntry<K extends string>({
  bucket,
  count,
}: {
  bucket: AgingBucketView<K>;
  count: number;
}) {
  return (
    <>
      <span
        aria-hidden
        className={`mr-1 inline-block size-2 rounded-full ${bucket.barClass}`}
      />
      <span className={`font-semibold ${bucket.textClass}`}>{count}</span>{' '}
      <T context="AR tasks aging bucket label, a day range such as 0-2 days">
        <Var>{bucket.key}</Var>d
      </T>
    </>
  );
}

function AgingLegend<K extends string>({
  aging,
  buckets,
  active,
  onPick,
}: AgingPickProps<K>) {
  return (
    <div
      className="grid text-[11px] text-muted-foreground"
      style={{
        gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))`,
      }}
    >
      {buckets.map((bucket) =>
        onPick ? (
          <button
            key={bucket.key}
            type="button"
            aria-pressed={active === bucket.key}
            onClick={() => onPick(bucket.key)}
            className={`-mx-1 cursor-pointer rounded px-1 text-left hover:bg-muted hover:text-foreground ${
              active === bucket.key
                ? 'bg-muted underline underline-offset-2'
                : ''
            }`}
          >
            <LegendEntry bucket={bucket} count={aging[bucket.key]} />
          </button>
        ) : (
          <span key={bucket.key} className="-mx-1 px-1">
            <LegendEntry bucket={bucket} count={aging[bucket.key]} />
          </span>
        ),
      )}
    </div>
  );
}

/**
 * Aging metric card: a stacked bar and legend of open items by age bucket,
 * with an optional red callout beside the title. With `onPick`, clicking a
 * segment or any count (zero too) picks that bucket (e.g. to filter a
 * table); without it the card is read-only.
 */
export function AgingCard<K extends string>({
  aging,
  buckets,
  callout,
  title,
  active,
  onPick,
}: {
  aging: Record<K, number>;
  buckets: ReadonlyArray<AgingBucketView<K>>;
  /** Shown in red beside the title, e.g. how many are overdue. */
  callout?: ReactNode;
  /** Card heading; defaults to "Aging". */
  title?: ReactNode;
  active?: K;
  onPick?: (bucket: K) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm font-medium text-muted-foreground">
          {title ?? (
            <T context="AR tasks metric card title, age of open tasks">Aging</T>
          )}
          {callout && (
            <span className="text-xs font-semibold text-red-700">
              {callout}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <AgingBar
          aging={aging}
          buckets={buckets}
          active={active}
          onPick={onPick}
        />
        <AgingLegend
          aging={aging}
          buckets={buckets}
          active={active}
          onPick={onPick}
        />
      </CardContent>
    </Card>
  );
}

/** A task type's aging card: 0-2, 3-7, 8-14 and 15+ days, flagging 15+. */
export function TaskAgingCard({
  aging,
  title,
  active,
  onPick,
}: {
  aging: TaskAgingCounts;
  /** Card heading; defaults to "Aging". */
  title?: ReactNode;
  active?: TaskMetricAgingBucket;
  onPick?: (bucket: TaskMetricAgingBucket) => void;
}) {
  return (
    <AgingCard
      aging={aging}
      buckets={TASK_AGING_BUCKETS}
      title={title}
      active={active}
      onPick={onPick}
      callout={
        aging['15+'] > 0 && (
          <T context="AR tasks aging card, count of tasks pending 15+ days">
            <Var>{aging['15+']}</Var> over 15 days
          </T>
        )
      }
    />
  );
}
